import { PermissionModel } from "@/Domains/Permission/Logic";
import { useAppSelector } from "@/OneLiners";
import { AppService, RecordMap, RoleHelper, useAppEnum } from "@/Shared";
import { AccessRightModel } from "../../../Permission/Logic/Models/AccessRightModel";


/**
 * =====================================================
 * Hook: useAccessRight
 * =====================================================
 *
 * Description:
 * Custom hook to provide access control logic for the current user
 * based on a list of PermissionModel entries.
 *
 * Responsibilities:
 * - Retrieve the current user's role from the session state
 * - Validate roles, resources, and action types against enums
 * - Provide generic and specific access check functions:
 *   canRead, canWrite, canDelete, canExecute, canExport, canRestore, canAudit
 * - Automatically grants full access to Sudo or higher roles
 *
 * Notes:
 * - All permission checks rely on backend-provided PermissionModel data
 * - Invalid roles, resources, or actions log warnings to console
 * - Designed for seamless integration with UI components and services
 *
 * Author: Samano CASTRE
 * Date: January 31, 2026
 */
export const useAccessRight = (permissionMap: RecordMap<PermissionModel>): AccessRightModel => {

    //const { enums } = useAppSelector(state => state.appState);

    /** =====================================================
     * Current User & Enums
     * =====================================================
     *
     * Retrieves the current user from the Redux store and
     * pulls the Permission-related enums for validation.
     */
    const currentUser = useAppSelector(state => state.sessionState.userSession?.createdByUser);
    const { PermissionEnums, hasEnums } = useAppEnum();


    /** =====================================================
     * Type Aliases for Permissions
     * =====================================================
     *
     * Strongly typed values for actions, roles, and resources
     * using PermissionEnums to reduce runtime errors.
     */
    type ActionValue = typeof PermissionEnums.AccessTypeEnum[number]["value"];
    type RoleValue = typeof PermissionEnums.UserRoleEnum[number]["value"];
    type ResourceValue = typeof PermissionEnums.ResourceTypeEnum[number]["value"];


    /** =====================================================
    * Validation Helpers
    * =====================================================
    *
    * Functions to check if roles, resources, and actions
    * are valid according to PermissionEnums.
    */
    const isValidRole = (role: RoleValue) => PermissionEnums.UserRoleEnum.some(uRole => uRole.value === role);
    const isValidResource = (resource: ResourceValue) => PermissionEnums.ResourceTypeEnum.some(rType => rType.value === resource);
    const isValidAction = (action: ActionValue) => PermissionEnums.AccessTypeEnum.some(aType => aType.value === action);


    /** =====================================================
     * Generic Access Check
     * =====================================================
     *
     * Determines if the current user can perform a specific
     * action on a given resource.
     *
     * Steps:
     * 1. Validate the user's role exists
     * 2. Validate the resource and action
     * 3. Grant full access for Sudo or higher roles
     * 4. Check PermissionModel list for specific access rights
     *
     * Logs warnings for any invalid inputs to help debugging.
     */
    const canAccess = (resource: ResourceValue, action: ActionValue) => {
        if(!hasEnums) return false;
        const userRole = currentUser?.role;

        if (!userRole) {
            console.warn("[AccessRight] Role not found for the current user:", { action, resource });
            return false;
        }
        
        if (!isValidRole(userRole)) {
            console.warn("[AccessRight] Invalid role : ", { action, resource, userRole });
            return false;
        }

        if (!isValidResource(resource)) {
            console.warn("[AccessRight] Invalid resource ", { action, resource, userRole });
            return false;
        }

        if (!isValidAction(action)) {
            console.warn("[AccessRight] Invalid access action ", { action, resource, userRole });
            return false;
        }

        // Full access for Sudo users
        if (RoleHelper.isAtLeastSuperAdmin(userRole)) {
            return true;
        }

        const rolePermissions = permissionMap.find(permission => permission.role === userRole);

        const resourceAccess = rolePermissions?.resourceAccessList
            ?.find(rAccess => rAccess.resource === resource);

        return !!resourceAccess?.rightList?.includes(String(action));
    };


    /** =====================================================
    * Specific Access Helpers
    * =====================================================
    *
    * Convenient functions for common permission checks.
    * Wraps the generic canAccess function with predefined actions.
    */
    const canWrite = (resource: ResourceValue) => canAccess(resource, 'write');
    const canRead = (resource: ResourceValue) => canAccess(resource, 'read');
    const canDelete = (resource: ResourceValue) => canAccess(resource, 'delete');
    const canExecute = (resource: ResourceValue) => canAccess(resource, 'execute');
    const canExport = (resource: ResourceValue) => canAccess(resource, 'export');
    const canRestore = (resource: ResourceValue) => canAccess(resource, 'restore');
    const canAudit = (resource: ResourceValue) => canAccess(resource, 'audit');


    /** =====================================================
     * Return API
     * =====================================================
     *
     * Exposes enums, current user, generic and specific
     * access check functions for consuming components/services.
     */
    return {
        PermissionEnums,
        currentUser,
        canAccess,
        canWrite,
        canRead,
        canDelete,
        canExecute,
        canRestore,
        canExport,
        canAudit,
        hasEnums
    };
};
